import dayjs from 'dayjs';

import {
  CloneCreateResponse,
  CloneJobItem,
  CloneJobItemReason,
  CloneJobItemStatus,
  CloneJobPhase,
  CloneJobStatus,
} from 'lib/api/cloneLoads';

const reasonLabels: Partial<Record<CloneJobItemReason, string>> = {
  [CloneJobItemReason.DuplicateInRequest]: 'Duplicate in email',
  [CloneJobItemReason.DuplicateExisting]: 'Already exists',
  [CloneJobItemReason.DuplicateInProgress]: 'Already being created',
  [CloneJobItemReason.MissingReference]: 'Missing reference',
  [CloneJobItemReason.InvalidRow]: 'Could not build this load',
  [CloneJobItemReason.GuardrailRejected]: 'Needs review',
  [CloneJobItemReason.DuplicateCheckFailed]: 'Could not check existing loads',
  [CloneJobItemReason.ReservationFailed]: 'Could not reserve this load',
  [CloneJobItemReason.ReservationLost]: 'Load reservation was lost',
  [CloneJobItemReason.AmbiguousWrite]:
    'Creation may have succeeded. Verify in the TMS.',
  [CloneJobItemReason.TMSCreateFailed]: 'TMS creation failed',
  [CloneJobItemReason.JobFailedBeforeCreate]: 'Not attempted',
};

export function activeHeading(job: CloneCreateResponse): string {
  switch (job.phase) {
    case CloneJobPhase.Queued:
      return 'Starting load cloning...';
    case CloneJobPhase.Extracting:
      return 'Reading email...';
    case CloneJobPhase.Creating:
      return 'Creating loads';
    default:
      return 'Creating loads';
  }
}

export function activeDescription(job: CloneCreateResponse): string {
  if (
    job.phase === CloneJobPhase.Creating &&
    job.progress.total !== null &&
    job.progress.total > 0
  ) {
    return `${job.progress.completed} of ${job.progress.total} complete`;
  }
  if (job.phase === CloneJobPhase.Extracting) {
    return 'Finding the loads to create.';
  }
  return 'You can work elsewhere while this continues.';
}

export function terminalHeading(job: CloneCreateResponse): string {
  if (job.status === CloneJobStatus.Failed) {
    return 'Load cloning failed';
  }
  if (allRowsAreDuplicates(job)) {
    return 'No new loads created';
  }
  if (
    job.summary.created > 0 &&
    onlyDuplicateSkips(job) &&
    job.extractionWarnings.length === 0
  ) {
    return `${job.summary.created} ${pluralize('load', job.summary.created)} created`;
  }
  if (jobHasIssues(job)) {
    return 'Load cloning finished with notes';
  }
  return 'Load cloning complete';
}

export function jobHasIssues(job: CloneCreateResponse): boolean {
  return (
    job.status === CloneJobStatus.Failed ||
    job.status === CloneJobStatus.Partial ||
    job.summary.failed > 0 ||
    job.summary.skipped > 0 ||
    job.extractionWarnings.length > 0
  );
}

export function terminalSummary(job: CloneCreateResponse): string {
  const { created, skipped, failed } = job.summary;
  const parts: string[] = [];
  if (
    created > 0 &&
    !(onlyDuplicateSkips(job) && job.extractionWarnings.length === 0)
  ) {
    parts.push(`${created} ${pluralize('load', created)} created`);
  }
  const existing = job.items.filter(
    (item) =>
      item.status === CloneJobItemStatus.Skipped &&
      item.reason === CloneJobItemReason.DuplicateExisting
  ).length;
  if (allRowsAreDuplicates(job) && existing === job.items.length) {
    return existing === 1
      ? 'This PO already exists in your TMS.'
      : `All ${existing} POs already exist in your TMS.`;
  }
  if (created === 0 && skipped > 0 && !allRowsAreDuplicates(job))
    parts.push('No new loads created');
  if (existing > 0) parts.push(`${existing} already existed`);
  const creating = job.items.filter(
    (item) =>
      item.status === CloneJobItemStatus.Skipped &&
      item.reason === CloneJobItemReason.DuplicateInProgress
  ).length;
  const repeated = job.items.filter(
    (item) =>
      item.status === CloneJobItemStatus.Skipped &&
      item.reason === CloneJobItemReason.DuplicateInRequest
  ).length;
  if (creating > 0) parts.push(`${creating} already being created`);
  if (repeated > 0)
    parts.push(`${repeated} repeated ${pluralize('PO', repeated)}`);
  const otherSkipped = skipped - existing - creating - repeated;
  if (otherSkipped > 0) parts.push(`${otherSkipped} not created`);
  if (failed > 0) {
    parts.push(`${failed} failed`);
  }
  if (parts.length > 0) {
    return parts.join('. ') + '.';
  }
  return job.message || 'No loads were created.';
}

export function onlyDuplicateSkips(job: CloneCreateResponse): boolean {
  const skipped = job.items.filter(
    (item) => item.status === CloneJobItemStatus.Skipped
  );
  return (
    job.status !== CloneJobStatus.Failed &&
    job.summary.failed === 0 &&
    skipped.length > 0 &&
    skipped.length === job.summary.skipped &&
    skipped.every(
      (item) =>
        item.reason === CloneJobItemReason.DuplicateExisting ||
        item.reason === CloneJobItemReason.DuplicateInProgress ||
        item.reason === CloneJobItemReason.DuplicateInRequest
    )
  );
}

export function allRowsAreDuplicates(job: CloneCreateResponse): boolean {
  return (
    job.summary.created === 0 &&
    job.summary.failed === 0 &&
    job.status !== CloneJobStatus.Failed &&
    job.items.length > 0 &&
    job.items.every(
      (item) =>
        item.status === CloneJobItemStatus.Skipped &&
        [
          CloneJobItemReason.DuplicateExisting,
          CloneJobItemReason.DuplicateInProgress,
          CloneJobItemReason.DuplicateInRequest,
        ].includes(item.reason!)
    )
  );
}

export function itemMessage(item: CloneJobItem): string {
  switch (item.reason) {
    case CloneJobItemReason.DuplicateExisting:
      return 'This load already exists in your TMS. Nothing new was created.';
    case CloneJobItemReason.DuplicateInProgress:
      return 'This load is already being created by another clone request.';
    case CloneJobItemReason.DuplicateInRequest:
      return "This PO is repeated in the request. This extra occurrence wasn't created.";
    case CloneJobItemReason.MissingReference:
      return "No PO or load number found. This row wasn't created.";
    default: {
      const message = item.message ?? '';
      const prefix = `${item.reference}: `;
      return message.startsWith(prefix)
        ? message.slice(prefix.length)
        : message;
    }
  }
}

export function itemResultLabel(item: CloneJobItem): string {
  const reasonLabel = item.reason ? reasonLabels[item.reason] : undefined;
  if (reasonLabel) {
    return reasonLabel;
  }
  switch (item.status) {
    case CloneJobItemStatus.Created:
      return 'Created';
    case CloneJobItemStatus.Skipped:
      return 'Skipped';
    case CloneJobItemStatus.Failed:
      return 'Failed';
  }
}

export function shouldShowItemMessage(
  item: CloneJobItem
): item is CloneJobItem & { message: string } {
  return Boolean(
    itemMessage(item) &&
    item.reason !== CloneJobItemReason.AmbiguousWrite &&
    item.reason !== CloneJobItemReason.ReservationLost
  );
}

export function formatCompletedAt(value: string): string {
  const completedAt = dayjs(value);
  if (!completedAt.isValid()) {
    return 'Completed';
  }
  if (completedAt.isSame(dayjs(), 'day')) {
    return `Completed today at ${completedAt.format('h:mm A')}`;
  }
  return `Completed ${completedAt.format('MMM D, YYYY [at] h:mm A')}`;
}

function pluralize(word: string, count: number): string {
  return count === 1 ? word : `${word}s`;
}
